// =============================================================================
// FenX Custom - Google Click ID (gclid) capture  [analytics-phase-1:foundation]
// =============================================================================
//
// Purpose:
//   Capture the Google Ads click id (gclid / gbraid / wbraid) at the ad-click
//   landing and carry it forward so every resulting order records which click
//   produced it. Chain: URL param -> consent-gated first-party cookie -> Shopify
//   cart attribute -> order note attribute -> backend. This is the CAPTURE half only;
//   the LEVERAGE half (Enhanced Conversions / Offline Conversion Import upload)
//   is analytics-phase-3:server-side-tracking.
//
//   WHICH identifiers were captured is carried, not just their values, and ALL of them
//   are kept -- gclid and gbraid can both be present on one click. Stored form is
//   "name=value" pairs joined by "&", under the key `fenx_click_ids`. See
//   encodeClickIds() for the Ads API citation behind both choices.
//
//   The key is `fenx_click_ids`, not `fenx_gclid`: it holds any of three identifiers and
//   sometimes two at once, so naming it after one of them mislabels its own contents.
//   Renamed 2026-08-07, before any deployment, so there is no stored data to migrate.
//
// Why capture at launch (not later):
//   A consented conversion whose cookie/thank-you page is lost is recoverable
//   only if the click id was stored at click time. If we do not capture now, that
//   slice is gone forever -- you cannot backfill a click id after the fact.
//
// Platform:
//   Storefront theme snippet on fenxcustom.com (NOT the Web Pixels sandbox -- the
//   sandbox cannot set cart attributes on the main cart). Load it on all
//   storefront pages, after the consent banner (Step 1.5:consent-mode-and-banner) has initialised.
//
// Consent model (capture-then-promote; purge AND retract on deny):
//   On landing the id is stashed in sessionStorage as PENDING (pre-consent, so it
//   survives the visitor browsing a page or two before answering the banner). It
//   is written to the durable 90-day cookie and pushed to the Shopify cart ONLY
//   when Google Ads consent (ad_storage + ad_user_data + ad_personalization) is granted.
//   All three, because the server derives ad_user_data AND ad_personalization from this
//   write existing -- a two-signal gate would report a grant nobody gave. On denial the
//   pending stash and cookie are purged AND the cart attribute is cleared (set to
//   ""), so an id captured under an earlier grant does not survive a later revoke.
//
// Consent source -- WHATEVER MECHANISM IS LIVE, in this order:
//   1. `window.fenxCookieConsent` (DD-008:cookie-consent-persistence, the in-house
//      banner). Preferred when present. NOT BUILT as of 2026-08-07.
//   2. Shopify's Customer Privacy API, which is what is actually live on both stores
//      (Shopify's own banner, since 2026-08-06).
// Requirements Section 12.2 makes reading the live mechanism binding, and forbids
// hard-coding one banner product as the only source. An earlier version of this file
// read only (1); against production it polled for ten seconds, timed out, and captured
// nothing.
//
// Shopify API facts, from https://shopify.dev/docs/api/customer-privacy (read 2026-08-07)
// -- each of these is load-bearing and none is guessable:
//   - `Shopify.customerPrivacy` is NOT present by default. It must be loaded first:
//     `Shopify.loadFeatures([{name:'consent-tracking-api', version:'0.1'}], cb)`.
//   - `currentVisitorConsent()` returns {marketing, analytics, preferences, sale_of_data},
//     each 'yes' | 'no' | '' -- '' means UNDECIDED, not denied.
//   - `visitorConsentCollected` fires ONLY on a change, never on listener attach. So the
//     current state must be read directly as well as subscribed to.
//   - `marketing` is the advertising signal; it maps to Consent Mode's ad_storage /
//     ad_user_data / ad_personalization. `analytics` maps to analytics_storage.
//
// Tri-state, because two-state loses data:
//   granted   -> promote to cookie + cart
//   denied    -> purge and retract
//   undecided -> DO NOTHING. The pending id stays in sessionStorage awaiting an answer.
// Collapsing undecided into denied would purge a just-captured click id before the
// visitor ever sees the banner.
//
// Config:
//   - COOKIE_MAX_AGE_DAYS = 90: matches Google's own `_gcl_aw` cookie default and
//     covers the Google Ads maximum conversion window, so a consented conversion
//     is not lost to cookie expiry earlier than Ads would still credit it.
// =============================================================================

(function () {
  "use strict";

  var CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid"]; // checked in this order
  var COOKIE_NAME = "fenx_click_ids";
  var CART_ATTRIBUTE_KEY = "fenx_click_ids";
  var PENDING_KEY = "fenx_click_ids_pending"; // pre-consent transient hold
  var SYNCED_KEY = "fenx_click_ids_synced"; // value already pushed to cart this session
  var DENIED_CLEARED_KEY = "fenx_click_ids_denied_cleared"; // cart cleared once on deny
  var COOKIE_MAX_AGE_DAYS = 90;

  // --- Pure helpers (no I/O) -------------------------------------------------

  // EVERY click-id param present in a query string, in CLICK_ID_PARAMS order. Empty array
  // when none. Not "the first one": gclid and gbraid can both be present on one click, and
  // the Ads API wants both (see encodeClickIds).
  function extractClickIds(search) {
    var params = new URLSearchParams(search || "");
    var found = [];
    for (var i = 0; i < CLICK_ID_PARAMS.length; i++) {
      var value = params.get(CLICK_ID_PARAMS[i]);
      if (value) {
        found.push({ name: CLICK_ID_PARAMS[i], value: value });
      }
    }
    return found;
  }

  // Consent Mode v2: all THREE advertising signals must be granted.
  //
  // ad_personalization is in this gate because of what the SERVER does downstream, not
  // because storing a click id needs it. The server derives its Measurement Protocol
  // consent block from this cart attribute existing, and that block asserts BOTH
  // ad_user_data and ad_personalization. If the gate did not test ad_personalization, a
  // visitor who granted the other two and denied that one would still get a click id
  // written, and the server would then report a grant it never observed.
  //
  // Shopify's single `marketing` signal maps to all three, so this changes nothing on that
  // path. It matters on the first-party path (window.fenxCookieConsent), which stores the
  // four signals separately and can express exactly this combination.
  function isAdConsentGranted(consent) {
    return (
      !!consent &&
      consent.ad_storage === "granted" &&
      consent.ad_user_data === "granted" &&
      consent.ad_personalization === "granted"
    );
  }

  // --- Consent integration (customization points) ---------------------------

  // Tri-state: "granted" | "denied" | "unknown". Never collapse unknown into denied.
  function readAdConsent() {
    // 1. In-house banner, preferred when it exists.
    var fenx = window.fenxCookieConsent && window.fenxCookieConsent.state;
    if (fenx) {
      if (isAdConsentGranted(fenx)) return "granted";
      // Tri-state here too. Consent Mode defaults every signal to denied BEFORE the
      // visitor answers, so "not granted" is not the same as "refused" -- reading it as
      // a refusal would purge a just-captured click id before the banner was ever seen,
      // the exact collapse this file's header forbids on the Shopify path.
      return fenx.answered ? "denied" : "unknown";
    }
    // 2. Shopify Customer Privacy API. `marketing` is the advertising signal.
    var sp = window.Shopify && window.Shopify.customerPrivacy;
    if (sp && typeof sp.currentVisitorConsent === "function") {
      var marketing = sp.currentVisitorConsent().marketing;
      if (marketing === "yes") return "granted";
      if (marketing === "no") return "denied";
      return "unknown"; // '' -- visitor has not answered the banner yet
    }
    return "unknown"; // no mechanism resolved
  }

  // ponytail: load-once guard. loadFeatures is idempotent per Shopify, but a second
  // call still costs a round trip on every page.
  var shopifyApiRequested = false;

  // Load Shopify's consent API (absent by default), then call back. Never throws.
  function withShopifyConsentApi(callback) {
    var sp = window.Shopify && window.Shopify.customerPrivacy;
    if (sp && typeof sp.currentVisitorConsent === "function") {
      callback(true);
      return;
    }
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== "function") {
      callback(false);
      return;
    }
    if (shopifyApiRequested) {
      callback(false);
      return;
    }
    shopifyApiRequested = true;
    try {
      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        function (err) {
          callback(!err && !!window.Shopify.customerPrivacy);
        }
      );
    } catch {
      callback(false);
    }
  }

  // Resolve consent now, and again on every later change, from whichever mechanism is live.
  //
  // Do NOT treat "no mechanism yet" as denied. That would purge a just-captured pending
  // click id before the visitor ever answers the banner -- a real load-order race when this
  // snippet runs before the banner bootstraps. On timeout the state stays "unknown", which
  // promotes nothing and purges nothing: the id lives only in sessionStorage and is never
  // written to the cookie or cart without a real grant.
  function onConsentResolved(callback) {
    // Later changes, from either mechanism. Registered once, up front, because
    // `visitorConsentCollected` does not replay for a listener that attaches late.
    document.addEventListener("visitorConsentCollected", function () {
      callback();
    });

    if (window.fenxCookieConsent && typeof window.fenxCookieConsent.onResolved === "function") {
      window.fenxCookieConsent.onResolved(callback);
      return;
    }

    withShopifyConsentApi(function (ready) {
      if (ready) {
        callback(); // current state; changes arrive on the listener above
        return;
      }
      // Neither mechanism is up yet. Poll for either, then resolve once.
      var waited = 0;
      var POLL_MS = 200;
      var MAX_WAIT_MS = 10000;
      var timer = setInterval(function () {
        var fenxUp =
          window.fenxCookieConsent && typeof window.fenxCookieConsent.onResolved === "function";
        var shopifyUp =
          window.Shopify &&
          window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.currentVisitorConsent === "function";
        if (fenxUp) {
          clearInterval(timer);
          window.fenxCookieConsent.onResolved(callback);
        } else if (shopifyUp) {
          clearInterval(timer);
          callback();
        } else if ((waited += POLL_MS) >= MAX_WAIT_MS) {
          clearInterval(timer);
          // Record the timeout rather than exiting silently: a script that never decided
          // is indistinguishable from one that never ran (Requirements Section 12.2).
          console.warn(
            "[FenX gclid] no consent mechanism resolved in " +
              MAX_WAIT_MS +
              "ms; " +
              "treating as undecided, pending id left intact"
          );
        }
      }, POLL_MS);
    });
  }

  // --- Session store (pre-consent transient; fails safe if unavailable) ------

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* private mode / disabled: capture degrades to same-page only */
    }
  }

  function sessionRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* no-op */
    }
  }

  // --- Cookie + cart I/O -----------------------------------------------------

  function writeCookie(value) {
    var maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    document.cookie =
      COOKIE_NAME +
      "=" +
      encodeURIComponent(value) +
      "; Max-Age=" +
      maxAge +
      "; Path=/; SameSite=Lax; Secure";
  }

  function readCookie() {
    var match = document.cookie.match(new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function purgeCookie() {
    document.cookie = COOKIE_NAME + "=; Max-Age=0; Path=/; SameSite=Lax; Secure";
  }

  // Shopify AJAX Cart API: POST /cart/update.js with an `attributes` object,
  // which MERGES the given keys (other cart attributes are untouched). Setting
  // the value to "" clears just this attribute. The attribute surfaces on the
  // order as a note attribute, which the order handler reads directly. There is no
  // promotion step and no order metafield.
  async function setCartAttribute(value) {
    var res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { [CART_ATTRIBUTE_KEY]: value } }),
    });
    if (!res || !res.ok) {
      throw new Error("cart/update.js failed: " + (res ? res.status : "no response"));
    }
    return res;
  }

  // --- Orchestration ---------------------------------------------------------

  // Stored form is "name=value" pairs joined by "&", e.g. "gclid=abc&gbraid=xyz".
  // NOT a bare value, and NOT a single pair.
  //
  // Source: Google Ads API, ClickConversion
  // https://developers.google.com/google-ads/api/docs/conversions/upload-clicks
  // (read 2026-08-07). Two facts from it, both load-bearing:
  //
  //   1. There is a SEPARATE upload field per identifier -- `gclid`, `gbraid`, `wbraid`.
  //      An upload must say which it is carrying, so a bare value cannot be uploaded at
  //      all. The field names above are the exact API identifiers; keep our stored names
  //      equal to them so the mapping needs no lookup table.
  //
  //   2. More than one can be present on the same click, and Google's own guidance is to
  //      set BOTH when they are: "In some cases you may be able to associate both a gclid
  //      and a gbraid with a conversion. In such cases we recommend setting both." So
  //      first-match-wins would silently discard the second and lose attribution.
  //
  // Directions, per the same API reference -- the Ads help centre summarises these loosely
  // enough to read backwards, so cite the API page, not the help page:
  //   gclid  -- ordinary click
  //   gbraid -- clicked an ad on the WEB, directed into the iOS APP
  //   wbraid -- clicked an ad in the iOS APP, directed to the WEB
  //
  // The encoding rides the whole chain unchanged: sessionStorage -> cookie -> cart
  // attribute -> order note attribute -> backend, which parses it with URLSearchParams.
  // That is why "&" and "=" rather than a bespoke separator: the parser already exists on
  // both ends and the values are URL-safe.
  function encodeClickIds(captured) {
    var parts = [];
    for (var i = 0; i < captured.length; i++) {
      parts.push(captured[i].name + "=" + encodeURIComponent(captured[i].value));
    }
    return parts.join("&");
  }

  function captureOnLanding() {
    var captured = extractClickIds(window.location.search);
    if (captured.length) {
      sessionSet(PENDING_KEY, encodeClickIds(captured));
    }
  }

  async function onGranted(value) {
    // Write the durable cookie FIRST: it is the retry source. If the cart write
    // below fails, SYNCED_KEY stays unset and the next page/consent event re-reads
    // this cookie and retries — nothing is lost.
    writeCookie(value);
    if (sessionGet(SYNCED_KEY) !== value) {
      try {
        await setCartAttribute(value); // push once per (session, value): avoids per-page POSTs
        sessionSet(SYNCED_KEY, value); // mark synced ONLY after the write is confirmed
      } catch (err) {
        console.warn("[FenX gclid] cart attribute write failed, will retry next page:", err);
      }
    }
    sessionRemove(PENDING_KEY);
    sessionRemove(DENIED_CLEARED_KEY);
  }

  async function onDenied() {
    purgeCookie();
    sessionRemove(PENDING_KEY);
    sessionRemove(SYNCED_KEY);
    // Retract any cart attribute set under an earlier grant -- once per session.
    // Mark cleared ONLY after the clear is confirmed, so a failed clear retries.
    if (sessionGet(DENIED_CLEARED_KEY) !== "1") {
      try {
        await setCartAttribute("");
        sessionSet(DENIED_CLEARED_KEY, "1");
      } catch (err) {
        console.warn("[FenX gclid] cart attribute clear failed, will retry next page:", err);
      }
    }
  }

  async function resolve() {
    var state = readAdConsent();
    if (state === "granted") {
      var value = sessionGet(PENDING_KEY) || readCookie();
      if (value) {
        await onGranted(value);
      }
    } else if (state === "denied") {
      await onDenied();
    }
    // "unknown": the visitor has not answered. Promote nothing, purge nothing.
  }

  function main() {
    captureOnLanding();
    onConsentResolved(resolve);
  }

  main();
})();
